import { createFileRoute, Link, useNavigate, useSearch, redirect } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PoForm } from "@/features/purchase-orders/PoForm";
import { useIndent } from "@/features/indents/api";
import { apiGet, apiList } from "@/integrations/api/client";
import type { PoFormValues } from "@/features/purchase-orders/types";
import { canCreatePOFromIndent } from "@/lib/roles";
import { currentUserQueryOptions } from "@/hooks/useCurrentUser";

type Search = { fromIndent?: string; fromAward?: string };

export const Route = createFileRoute("/_authenticated/purchase-orders/new")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    fromIndent: typeof s.fromIndent === "string" ? s.fromIndent : undefined,
    fromAward: typeof s.fromAward === "string" ? s.fromAward : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(currentUserQueryOptions());
    if (!canCreatePOFromIndent(me.roles)) throw redirect({ to: "/purchase-orders" });
  },
  component: NewPoPage,
});

function useAwardBundle(awardId: string | undefined) {
  return useQuery({
    queryKey: ["po-from-award", awardId],
    enabled: !!awardId,
    queryFn: async () => {
      try {
        const award = await apiGet<any>(`/api/v1/procurement/awards/${awardId}/`);
        if (!award) return null;

        const rfq = await apiGet<any>(`/api/v1/procurement/rfqs/${award.rfq}/`);
        
        let quotation: any = null;
        if (award.quotation) {
          quotation = await apiGet<any>(`/api/v1/procurement/vendor-quotations/${award.quotation}/`);
        }

        let negotiation: any = null;
        if (award.negotiation) {
          negotiation = await apiGet<any>(`/api/v1/procurement/negotiations/${award.negotiation}/`);
        }

        // Reconstruct the payload to match what NewPoPage expects
        const constructedAward = {
          ...award,
          rfq_id: award.rfq,
          vendor_id: award.awarded_vendor,
          rfq: {
            ...rfq,
            indent: { id: rfq.indent },
            branch_id: rfq.branch?.id || rfq.branch
          }
        };

        const constructedQuotation = quotation ? {
          ...quotation,
          items: quotation.items?.map((qi: any) => {
            const rfqItem = (rfq.items || []).find((ri: any) => ri.id === qi.rfq_item_id);
            return {
              ...qi,
              rfq_item: {
                ...rfqItem,
                qty: rfqItem?.qty || 1,
                item_name: rfqItem?.item_name || rfqItem?.description || "",
                description: rfqItem?.description || "",
                purchase_indent_item: {
                  id: rfqItem?.custom_values?.purchase_indent_item_id || null,
                  asset_type: rfqItem?.custom_values?.asset_type || null,
                  asset_category_id: rfqItem?.custom_values?.asset_category_id || null,
                  remarks: null,
                }
              }
            };
          }) || []
        } : null;

        const constructedNegotiation = negotiation ? {
          negotiated_amount: negotiation.negotiated_amount,
          line_prices: negotiation.items?.map((ni: any) => ({
            rfq_item_id: ni.rfq_item,
            unit_price: ni.negotiated_price
          })) || []
        } : null;

        return {
          award: constructedAward,
          quotation: constructedQuotation,
          negotiation: constructedNegotiation
        };
      } catch (e) {
        console.error(e);
        return null;
      }
    },
  });
}

function NewPoPage() {
  const navigate = useNavigate();
  const { fromIndent, fromAward } = useSearch({ from: "/_authenticated/purchase-orders/new" });
  const { data: indent, isLoading: indentLoading } = useIndent(fromIndent);
  const { data: awardBundle, isLoading: awardLoading } = useAwardBundle(fromAward);

  // Prefer award → RFQ → indent lineage when fromAward is set
  const awardIndentId = (awardBundle?.award as { rfq?: { indent?: { id?: string } } } | undefined)?.rfq?.indent?.id;
  const { data: indentFromAward, isLoading: indentFromAwardLoading } = useIndent(awardIndentId);

  const isDataLoading = (fromIndent && indentLoading) || (fromAward && awardLoading) || (awardIndentId && indentFromAwardLoading);

  const effectiveIndent = indent ?? indentFromAward;
  const existingPoId = effectiveIndent?.purchase_order_id;

  let items: PoFormValues["items"] = [];
  let expectedDeliveryDate: string | undefined = undefined;
  if (awardBundle?.quotation) {
    type Line = {
      unit_price: number; tax_pct: number;
      rfq_item: { id: string; qty: number; item_name: string; description: string | null; spare?: string | null; line_no?: number | null;
        purchase_indent_item: { id: string; item_name?: string | null; asset_type?: string | null; asset_category_id?: string | null; item_description?: string | null; remarks?: string | null } | null;
      };
    };
    const lines = ((awardBundle.quotation as { items?: Line[] }).items ?? []);
    const negLines = (awardBundle.negotiation as { line_prices: { rfq_item_id: string; unit_price: number }[] | null } | null | undefined)?.line_prices ?? null;
    items = lines.map((ln) => {
      // Try matching by item_name from custom_values (more reliable than display name)
      const rfqCustom = (ln.rfq_item as any).custom_values || {};
      const piId = rfqCustom.purchase_indent_item_id;
      const pii = (piId && effectiveIndent?.items?.find((x: any) => x.id === piId))
        || effectiveIndent?.items?.find((x: any) => x.item_name === (rfqCustom.item_name || ln.rfq_item.item_name))
        || effectiveIndent?.items?.[(ln.rfq_item.line_no || 1) - 1]
        || ln.rfq_item.purchase_indent_item;
      const negLine = negLines?.find((l: { rfq_item_id: string; unit_price: number }) => l.rfq_item_id === ln.rfq_item.id);
      const unitPrice = negLine ? Number(negLine.unit_price) : Number(ln.unit_price) || 0;
      // Prefer the raw item_name (free-text) over the category name
      const resolvedItemName = (pii as any)?.item_name || rfqCustom.item_name || ln.rfq_item.item_name || (pii as any)?.item_name || "";
      // asset_category_id: use subcategory if available, else empty string
      const resolvedCategoryId = rfqCustom.asset_category_id || (pii as any)?.asset_category_id || "";
      // parent_category_id: use from custom_values or indent item
      const resolvedParentCategoryId = rfqCustom.parent_category_id || (pii as any)?.parent_category_id || null;
      return {
        item_name: resolvedItemName,
        item_description: ln.rfq_item.description ?? "",
        asset_class: rfqCustom.asset_type || (pii as any)?.asset_type || "Equipment",
        asset_type: rfqCustom.asset_type || (pii as any)?.asset_type || "Equipment",
        asset_category_id: resolvedCategoryId,
        parent_category_id: resolvedParentCategoryId,
        spare_id: ln.rfq_item.spare ?? "",
        quantity_ordered: Number(ln.rfq_item.qty) || 1,
        original_qty: Number(ln.rfq_item.qty) || undefined,
        unit_price: unitPrice,
        tax_percentage: Number(ln.tax_pct) || 0,
        asset_creation_required: (effectiveIndent as any)?.indent_type === "asset",
        source_indent_id: effectiveIndent?.id,
        purchase_indent_item_id: (pii as any)?.id,
        remarks: [(pii as any)?.remarks, negLine ? "Negotiated price" : null].filter(Boolean).join(" • "),
      };
    });

    const maxDeliveryDays = lines.reduce((max, ln) => {
      const days = Number((ln as any).delivery_days) || 0;
      return days > max ? days : max;
    }, 0);
    if (maxDeliveryDays > 0) {
      const d = new Date();
      d.setDate(d.getDate() + maxDeliveryDays);
      expectedDeliveryDate = d.toISOString().split("T")[0];
    }

  } else if (effectiveIndent) {
    items = effectiveIndent.items && effectiveIndent.items.length > 0
      ? [...effectiveIndent.items].sort((a, b) => a.line_no - b.line_no).map((it) => ({
          item_name: it.item_name ?? it.asset_category?.name ?? "",
          item_description: it.item_description ?? "",
          asset_class: it.asset_type || "Equipment",
          asset_type: it.asset_type || "Equipment",
          asset_category_id: it.asset_category_id ?? "",
          parent_category_id: it.parent_category_id ?? null,
          spare_id: it.spare_id ?? (it as any).spare ?? "",
          quantity_ordered: Number(it.quantity_required) || 1,
          unit_price: Number(it.estimated_unit_cost) || 0,
          tax_percentage: 0,
          asset_creation_required: true,
          source_indent_id: effectiveIndent.id,
          purchase_indent_item_id: it.id,
          remarks: it.remarks ?? "",
        }))
      : [{
          item_name: effectiveIndent.item_desc,
          item_description: effectiveIndent.justification ?? "",
          asset_class: "Equipment",
          asset_type: "Equipment",
          asset_category_id: effectiveIndent.category_id ?? "",
          spare_id: (effectiveIndent as any).spare_id ?? "",
          quantity_ordered: Number(effectiveIndent.qty_required) || 1,
          unit_price: Number(effectiveIndent.estimated_unit_cost) || 0,
          tax_percentage: 0,
          asset_creation_required: true,
          source_indent_id: effectiveIndent.id,
          remarks: "",
        }];
  }

  const awardVendorId = (awardBundle?.award as { vendor_id?: string } | undefined)?.vendor_id;
  const awardBranchId = (awardBundle?.award as { rfq?: { branch_id?: string } } | undefined)?.rfq?.branch_id;
  const awardRfqId = (awardBundle?.award as { rfq_id?: string } | undefined)?.rfq_id;

  const prefill: Partial<PoFormValues> | undefined = effectiveIndent
    ? {
        po_type: ((effectiveIndent as any).indent_type as any) || "asset",
        vendor_id: awardVendorId ?? effectiveIndent.po_vendor_id ?? "",
        branch_id: effectiveIndent.branch_id ?? awardBranchId ?? "",
        department_id: effectiveIndent.department_id ?? "",
        source_indent_id: effectiveIndent.id,
        rfq_id: awardRfqId,
        items,
        ...(expectedDeliveryDate ? { expected_delivery_date: expectedDeliveryDate } : {}),
      }
    : (awardBundle && items.length > 0)
      ? {
          po_type: "asset",
          vendor_id: awardVendorId ?? "",
          branch_id: awardBranchId ?? "",
          rfq_id: awardRfqId,
          items,
          ...(expectedDeliveryDate ? { expected_delivery_date: expectedDeliveryDate } : {}),
        }
      : undefined;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/purchase-orders" })}>
        <ArrowLeft className="mr-1 h-4 w-4" />Back
      </Button>
      {isDataLoading ? null : (
        <>
          {existingPoId && (
            <Alert>
              <AlertTitle>Purchase Order already exists for this indent</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>Only one PO per indent is allowed in this iteration.</span>
                <Button asChild size="sm" variant="outline">
                  <Link to="/purchase-orders/$poId" params={{ poId: existingPoId }}>
                    <ExternalLink className="mr-1 h-4 w-4" />Open Existing PO
                  </Link>
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {!existingPoId && <PoForm prefill={prefill} isVendorFixed={!!fromAward} />}
        </>
      )}
    </div>
  );
}
